"use client"

import { Badge, Button, Spinner } from "react-bootstrap"
import { Send } from "lucide-react"
import { useTranslation } from "../../../hooks/use-translation"
import type { SoftoneRequestRecord } from "../../../data/softone"

interface SoftoneRequestBadgeProps {
  request: SoftoneRequestRecord | undefined
  sending?: boolean
  // only offered for failed tickets, and waiting_link ones whose user has been linked since
  onSend?: () => void
}

/** Tickets table cell: was this ticket sent to SoftOne as a request? */
export function SoftoneRequestBadge({ request, sending, onSend }: SoftoneRequestBadgeProps) {
  const { t } = useTranslation()

  if (!request) return <span className="text-muted">-</span>

  const canSend = request.status === "failed" || (request.status === "waiting_link" && !!request.linkReady)

  const badge = (() => {
    switch (request.status) {
      case "sent":
        return (
          <Badge bg="success" title={`trdr ${request.trdr ?? "-"}`}>
            {t("softoneRequestSent", { id: String(request.soaction) })}
          </Badge>
        )
      case "sending":
        return (
          <Badge bg="info">
            <Spinner animation="border" size="sm" className="me-1" style={{ width: 10, height: 10 }} />
            {t("softoneRequestSending")}
          </Badge>
        )
      case "failed":
        return (
          <Badge bg="danger" title={request.error || ""}>
            {t("softoneRequestFailed")}
          </Badge>
        )
      case "waiting_link":
        return (
          <Badge bg="warning" text="dark" title={t("softoneRequestWaitingHelp")}>
            {t("softoneRequestWaiting")}
          </Badge>
        )
      case "disabled":
      default:
        return (
          <Badge bg="light" text="dark" className="border" title={t("softoneRequestDisabledHelp")}>
            {t("softoneRequestDisabled")}
          </Badge>
        )
    }
  })()

  return (
    <span className="d-inline-flex align-items-center gap-1 text-nowrap" onClick={(e) => e.stopPropagation()}>
      {badge}
      {canSend && onSend && (
        <Button
          variant="outline-primary"
          size="sm"
          className="py-0 px-1"
          onClick={onSend}
          disabled={sending}
          title={t("softoneRequestSend")}
          aria-label={t("softoneRequestSend")}
        >
          {sending ? <Spinner animation="border" size="sm" /> : <Send size={14} />}
        </Button>
      )}
    </span>
  )
}
